import { CompanyDTO } from "@shaarp/common";
import { Table } from "@tanstack/react-table";
import { t } from "i18next";
import { useEffect } from "react";
import { DataTablePagination } from "../data-table/data-table-pagination";
import { DataTableToolbar } from "../data-table/data-table-toolbar";
import { Skeleton } from "../ui/skeleton";
import { DataTableCompaniesCard } from "./data-table-companies-card";

interface DataTableCompaniesMobileProps {
  table: Table<CompanyDTO>;
  selected?: CompanyDTO;
  isLoading?: boolean;
  pagination?: boolean;
  showFilter?: boolean;
  additionalFilters?: React.ReactNode;
  onPaginationChange?: (pageIndex: number, pageSize: number) => void;
  rowClassNames?: (company: CompanyDTO) => string;
  toolbarContent?: React.ReactNode;
}

export function DataTableCompaniesMobile({
  table,
  selected,
  isLoading = false,
  pagination = true,
  showFilter = true,
  additionalFilters = <></>,
  onPaginationChange = () => {},
  rowClassNames = () => "",
  toolbarContent = null,
}: DataTableCompaniesMobileProps) {
  const tableState = table.getState();

  useEffect(() => {
    onPaginationChange(
      tableState.pagination.pageIndex,
      tableState.pagination.pageSize
    );
  }, [
    onPaginationChange,
    tableState.pagination.pageIndex,
    tableState.pagination.pageSize,
  ]);

  const filteredRows = table.getRowModel().rows;

  return (
    <div className="space-y-4">
      {showFilter && (
        <DataTableToolbar
          table={table}
          additionalActions={toolbarContent}
          additionalFilters={additionalFilters}
          disabled={isLoading}
        />
      )}

      <div>
        {isLoading && <CompanyCardSkeleton key={`skeleton-0`} />}

        {filteredRows.length > 0 ? (
          filteredRows.map((row) => (
            <DataTableCompaniesCard
              key={row.id}
              company={row.original}
              selected={selected}
              rowClassNames={rowClassNames}
            />
          ))
        ) : !isLoading ? (
          <div className="py-8 text-center text-muted-foreground">
            {t("components.dataTable.table.noResults")}
          </div>
        ) : null}

        {isLoading &&
          [...Array(4)].map((_, index) => (
            <CompanyCardSkeleton key={`skeleton-${index + 1}`} />
          ))}
      </div>

      <div className="flex flex-col gap-4 justify-between items-center sm:flex-row">
        <div className="w-full text-xs text-center text-muted-foreground sm:w-auto sm:text-left">
          <span>
            {t("components.dataTable.table.total")}{" "}
            <strong>{filteredRows.length}</strong>{" "}
            {t("components.dataTable.table.results_other", {
              count: filteredRows.length,
            })}
          </span>
        </div>
        {pagination &&
          (table.getRowModel().rows?.length > 10 ||
            table.getPageCount() > 1) && <DataTablePagination table={table} />}
      </div>
    </div>
  );
}

const CompanyCardSkeleton = () => {
  return (
    <div className="p-4 mb-4 rounded-lg border">
      <div className="flex gap-3 items-start">
        <Skeleton className="shrink-0 w-12 h-12 rounded-full" />
        <div className="flex-1 space-y-2">
          <Skeleton className="w-3/4 h-4 rounded" />
          <Skeleton className="w-1/2 h-3 rounded" />
          <Skeleton className="w-2/3 h-3 rounded" />
        </div>
      </div>
    </div>
  );
};
